import { useGetCommerceByIdQuery, useGetCommerceIntegrationWebhooksQuery } from '../../services';
import useConfirmDialog from '../IntegrationRequests/hooks/useConfirmDialog';
import useIntegrationWebhookActions from './hooks/useIntegrationWebhookActions';
import IntegrationWebhooksTable from './components/IntegrationWebhooksTable';
import IntegrationWebhookDialog from './components/IntegrationWebhookDialog';
import type { IntegrationWebhook } from '../../models/IntegrationWebhook';
import { CommerceSelectPanel } from '../CommerceIntegration/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GlobalButton, NavigateLinks } from '@/components';
import { commercePageBreadcrumbs } from '../../config';
import { INTEGRATION_WEBHOOKS_TITLE } from './config';
import { Add as AddIcon } from '@mui/icons-material';
import type { Commerce } from '../../models';
import { useMemo, useState } from 'react';

type DialogState = { webhook: IntegrationWebhook | null } | null;

const IntegrationWebhooksPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCommerce, setSelectedCommerce] = useState<Commerce | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const confirmation = useConfirmDialog();
  const actions = useIntegrationWebhookActions(confirmation.confirm);
  const breadcrumbs = useMemo(() => commercePageBreadcrumbs(navigate), [navigate]);

  const commerceId = searchParams.get('commerceId') ?? '';
  const isSelectedInUrl = selectedCommerce?.id === commerceId;
  const { data: urlCommerce } = useGetCommerceByIdQuery(
    { commerceId },
    { skip: !commerceId || isSelectedInUrl }
  );
  const shownCommerce = isSelectedInUrl ? selectedCommerce : (commerceId && urlCommerce) || null;
  const { data, isLoading, isFetching, error } = useGetCommerceIntegrationWebhooksQuery(
    commerceId,
    { skip: !commerceId }
  );

  const selectCommerce = (commerce: Commerce | null) => {
    setSelectedCommerce(commerce);
    setSearchParams(commerce ? { commerceId: commerce.id } : {});
  };

  return (
    <section className='flex flex-col gap-6 w-full h-full'>
      <NavigateLinks actualPage={INTEGRATION_WEBHOOKS_TITLE} breadcrumbs={breadcrumbs} />

      <CommerceSelectPanel selected={shownCommerce} onSelect={selectCommerce} />

      {commerceId && (
        <div className='w-full p-5 bg-white rounded-lg shadow-md flex flex-col gap-4'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='flex flex-col'>
              <h3 className='text-gray-800 font-semibold'>
                {data ? `Webhooks de ${data.commerce.name}` : 'Webhooks'}
              </h3>
              <p className='text-sm text-gray-500'>
                Se configuran por ambiente: en test se cargan desde el panel de test.
              </p>
            </div>
            <GlobalButton
              variant={GlobalButtonVariant.primaryButton}
              action={() => setDialog({ webhook: null })}
              disabled={!data || actions.isBusy}
            >
              <AddIcon />
              Agregar webhook
            </GlobalButton>
          </div>

          {error ? (
            <p className='text-center text-red-600 py-8'>No se pudieron cargar los webhooks</p>
          ) : (
            <IntegrationWebhooksTable
              webhooks={data?.webhooks ?? []}
              isLoading={isLoading || isFetching}
              disabled={actions.isBusy}
              onEdit={(webhook) => setDialog({ webhook })}
              onToggleActive={actions.toggleActive}
              onRemove={actions.remove}
            />
          )}
        </div>
      )}

      {dialog && data && (
        <IntegrationWebhookDialog
          commerceId={data.commerce.id}
          webhook={dialog.webhook}
          statuses={data.notifiableStatuses}
          isSaving={actions.isSaving}
          onClose={() => setDialog(null)}
          onCreate={actions.create}
          onUpdate={actions.update}
        />
      )}

      {confirmation.dialog}
    </section>
  );
};

export default IntegrationWebhooksPage;
